// ═══════════════════════════════════════════════════════════════
// RollCall — Events List
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Stack, Badge, Button, TextInput, Select,
} from '../../components/gab/GabComponents';
import { IconSearch, IconPlus } from '@tabler/icons-react';
import { useAuthStore } from '../../store/authStore';
import { useRollCallStore } from '../../store/rollcallStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { RC_EVENT_TYPE_META } from '../../types';
import type { RCEventType } from '../../types';

const TYPE_OPTIONS = [
  { value: 'all', label: 'All Types' },
  { value: 'shift', label: 'Shift' },
  { value: 'meal', label: 'Meal' },
  { value: 'training', label: 'Training' },
  { value: 'briefing', label: 'Briefing' },
  { value: 'custom', label: 'Custom' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'closed', label: 'Closed' },
];

export default function Events() {
  const navigate = useNavigate();
  const { canEdit } = useModulePermission('rollcall');
  const { incidents, loadIncidents } = useAuthStore();
  const { events, checkins, loading, fetchError, loadEvents, loadCheckins } = useRollCallStore();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [incidentFilter, setIncidentFilter] = useState('all');

  useEffect(() => { loadIncidents(); loadEvents(); loadCheckins(); }, []);

  const incidentOptions = useMemo(() => [
    { value: 'all', label: 'All Incidents' },
    ...incidents.filter(i => i.id != null).map(i => ({ value: String(i.id), label: i.name })),
  ], [incidents]);

  const filtered = useMemo(() => {
    let list = events;
    if (typeFilter !== 'all') list = list.filter(e => e.eventType === typeFilter);
    if (statusFilter !== 'all') list = list.filter(e => e.eventStatus === statusFilter);
    if (incidentFilter !== 'all') list = list.filter(e => String(e.incidentId) === incidentFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(e => (e.eventName || '').toLowerCase().includes(q));
    }
    return list.sort((a, b) => (b.datecreated || '').localeCompare(a.datecreated || ''));
  }, [events, typeFilter, statusFilter, incidentFilter, search]);

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Events</Title>
        {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />}
          onClick={() => navigate('/rollcall/events/new')}>New Event</Button>}
      </Group>

      <Group mb="md" gap="sm">
        <TextInput placeholder="Search events..." leftSection={<IconSearch size={14} />}
          value={search} onChange={e => setSearch(e.target.value)} size="xs" w={220} />
        <Select data={TYPE_OPTIONS} value={typeFilter}
          onChange={v => setTypeFilter(v ?? 'all')} size="xs" w={130} />
        <Select data={STATUS_OPTIONS} value={statusFilter}
          onChange={v => setStatusFilter(v ?? 'all')} size="xs" w={110} />
        <Select data={incidentOptions} value={incidentFilter}
          onChange={v => setIncidentFilter(v ?? 'all')} size="xs" w={200} />
      </Group>

      {fetchError ? (
        <Paper p="md" radius="sm" withBorder mb="md" style={{ borderColor: 'var(--color-error)' }}>
          <Group justify="space-between">
            <Text size="sm" c="var(--color-error)" fw={600}>Error loading events: {fetchError}</Text>
            <Button size="xs" variant="light" onClick={() => { loadEvents(); loadCheckins(); }}>Retry</Button>
          </Group>
        </Paper>
      ) : loading && !events.length ? (
        <Paper p="xl" radius="sm" withBorder>
          <Text ta="center" c="dimmed">Loading...</Text>
        </Paper>
      ) : filtered.length === 0 ? (
        <Paper p="xl" radius="sm" withBorder>
          <Text ta="center" c="dimmed">No events found.</Text>
        </Paper>
      ) : (
        <Stack gap="xs">
          {filtered.map(ev => {
            const inc = incidents.find(i => String(i.id) === String(ev.incidentId));
            const meta = RC_EVENT_TYPE_META[ev.eventType as RCEventType] || RC_EVENT_TYPE_META.custom;
            const evCheckins = checkins.filter(c => String(c.eventId) === String(ev.id));
            const currentlyIn = evCheckins.filter(c => !c.checkOutTime).length;
            return (
              <Paper key={ev.id} p="sm" radius="sm" withBorder
                style={{ cursor: 'pointer' }} onClick={() => navigate(`/rollcall/event/${ev.id}`)}>
                <Group justify="space-between">
                  <div>
                    <Group gap="xs" mb={2}>
                      <Text size="sm" fw={600}>{ev.eventName}</Text>
                      <Badge size="xs" color={meta.color}>{meta.label}
                        {ev.mealType ? ` (${ev.mealType})` : ''}</Badge>
                      <Badge size="xs" color={ev.eventStatus === 'open' ? 'var(--color-success)' : '#939598'}>
                        {ev.eventStatus}</Badge>
                    </Group>
                    <Text size="xs" c="dimmed">
                      {inc?.name || '—'} · {ev.startTime || '—'}
                      {ev.location ? ` · ${ev.location}` : ''}
                    </Text>
                  </div>
                  <Group gap="xs">
                    <Badge size="xs" variant="light" color="var(--color-primary-700)">{evCheckins.length} total</Badge>
                    {currentlyIn > 0 && (
                      <Badge size="xs" variant="light" color="var(--color-success)">{currentlyIn} in</Badge>
                    )}
                  </Group>
                </Group>
              </Paper>
            );
          })}
        </Stack>
      )}
    </div>
  );
}
